import { isAudioSubmitQuestion } from './questionModes.js';

function countBlankMarkers(value = '') {
  return (String(value).match(/\[blank\]/g) || []).length;
}

function splitWordBank(value = '') {
  return String(value)
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean);
}

function splitFilled(value = '', separator) {
  return String(value)
    .split(separator)
    .map((part) => part.trim());
}

function hasFilledSlots(value, separator, slotCount) {
  if (slotCount <= 0) return String(value || '').trim() !== '';
  const parts = splitFilled(value, separator);
  return parts.length === slotCount && parts.every(Boolean);
}

function recordingIsValid(id, audioRecordings = {}) {
  const recording = audioRecordings[id];
  return Boolean(recording) && recording.valid !== false;
}

function hasValidRecordingSlots(value, audioRecordings, slotCount) {
  const ids = splitFilled(value, ',').filter(Boolean);
  if (slotCount > 0 && ids.length !== slotCount) return false;
  if (slotCount <= 0 && ids.length === 0) return false;
  return ids.every((id) => recordingIsValid(id, audioRecordings));
}

function hasAllWordBankEntries(value, wordBank) {
  const normalize = (text) => String(text || '').trim().replace(/\s+/g, ' ');
  const entries = splitWordBank(wordBank).map(normalize);
  const answer = normalize(value);
  if (entries.length === 0) return answer !== '';
  if (!answer || answer.length !== entries.reduce((total, entry) => total + entry.length, entries.length - 1)) {
    return false;
  }

  // Match complete chips, not arbitrary substrings. Searching for "I" first
  // inside "I was" can consume part of the longer chip and make a fully
  // arranged answer look incomplete. Counts also preserve duplicate chips.
  const counts = new Map();
  entries.forEach((entry) => counts.set(entry, (counts.get(entry) ?? 0) + 1));
  const chips = [...counts.keys()];
  const failed = new Set();

  function matches(offset, remaining) {
    if (remaining === 0) return offset === answer.length;
    const key = `${offset}:${chips.map((chip) => counts.get(chip)).join(',')}`;
    if (failed.has(key)) return false;

    for (const chip of chips) {
      const count = counts.get(chip);
      if (count === 0 || !answer.startsWith(chip, offset)) continue;
      const end = offset + chip.length;
      if (end < answer.length && answer[end] !== ' ') continue;
      if (end === answer.length && remaining > 1) continue;

      counts.set(chip, count - 1);
      const nextOffset = end < answer.length ? end + 1 : end;
      const complete = matches(nextOffset, remaining - 1);
      counts.set(chip, count);
      if (complete) return true;
    }

    failed.add(key);
    return false;
  }

  return matches(0, entries.length);
}

export function isLessonAnswerComplete({
  question,
  currentAnswer = '',
  additionalAnswer = '',
  audioRecordings = {},
} = {}) {
  const type = question?.type;
  const blankCount = countBlankMarkers(question?.source_text_or_html);

  if (type === 'CLOZE') {
    return hasFilledSlots(currentAnswer, '#', blankCount);
  }

  if (type === 'Cloze_with_passage') {
    return (
      hasFilledSlots(additionalAnswer, '#', blankCount)
      && hasValidRecordingSlots(currentAnswer, audioRecordings, 1)
    );
  }

  if (type === 'Cloze_with_conversation') {
    return (
      hasFilledSlots(additionalAnswer, '#', blankCount)
      && hasValidRecordingSlots(currentAnswer, audioRecordings, blankCount)
    );
  }

  if (type === 'Wordcloud') {
    if (question?.domain === 'S') {
      return (
        hasAllWordBankEntries(additionalAnswer, question?.word_bank)
        && hasValidRecordingSlots(currentAnswer, audioRecordings, 0)
      );
    }
    return hasAllWordBankEntries(currentAnswer, question?.word_bank);
  }

  if (isAudioSubmitQuestion(question)) {
    return hasValidRecordingSlots(currentAnswer, audioRecordings, 0);
  }

  return String(currentAnswer || '').trim() !== '';
}
